import {
  AlertTriangle,
  Bell,
  Building2,
  CalendarClock,
  Check,
  ShieldAlert,
  Thermometer,
  Wifi,
  Power,
} from 'lucide-react'

import { ROUTES } from '../../constants'

// ──────────────────────────────────────────────────────────────────────────────
// Shared notification helpers
//
// Extracted from NotificationDropdown.jsx so the same row→item mapping and
// visual rules drive both the bell-icon dropdown AND the full-page
// Notifications list on /notifications. Keeping a single source
// of truth prevents the two surfaces from drifting (e.g. a new fan-out type
// added here flows to both at once).
// ──────────────────────────────────────────────────────────────────────────────

// Severity meta — icon, colour, and translucent background. Wins when a
// notification carries a recognised `severity` field.
export const SEVERITY_META = {
  CRITICAL: {
    label: 'Critical',
    color: 'var(--danger)',
    bg: 'rgba(255, 61, 87, 0.12)',
  },
  WARNING: {
    label: 'Warning',
    color: 'var(--warning)',
    bg: 'rgba(255, 166, 92, 0.14)',
  },
  INFO: {
    label: 'Info',
    color: 'var(--info)',
    bg: 'rgba(94, 166, 255, 0.14)',
  },
  OFFLINE: {
    label: 'Offline',
    color: 'var(--text-muted)',
    bg: 'rgba(164, 178, 205, 0.14)',
  },
  RESOLVED: {
    label: 'Resolved',
    color: 'var(--success)',
    bg: 'rgba(31, 216, 168, 0.14)',
  },
  TEMPERATURE: {
    label: 'Temperature',
    color: 'var(--warning)',
    bg: 'rgba(255, 166, 92, 0.14)',
  },
}

// Per-notification-type meta. Wins over `severity` when the backend sends
// a recognised `type` so the three branch-fan-out types (FACILITY_CREATED,
// CALIBRATION_EXPIRY, ALARM_PROFILE_STATUS_CHANGED) render with a type-
// specific icon, colour, and label rather than collapsing to the generic
// INFO chip. The `Icon` component reference lives in this map so callers
// pull it off the resolved severity object (see `severityForNotification`).
export const NOTIFICATION_TYPE_META = {
  FACILITY_CREATED: {
    label: 'New facility',
    color: 'var(--info)',
    bg: 'rgba(94, 166, 255, 0.14)',
    Icon: Building2,
  },
  CALIBRATION_EXPIRY: {
    label: 'Calibration',
    color: 'var(--warning)',
    bg: 'rgba(255, 166, 92, 0.14)',
    Icon: CalendarClock,
  },
  ALARM_PROFILE_STATUS_CHANGED: {
    label: 'Alarm profile',
    color: 'var(--primary)',
    bg: 'rgba(59, 130, 246, 0.14)',
    Icon: Power,
  },
}

// Map a backend `referenceType` to the SPA route for the resource.
// Kept aligned with src/routes/allRoutes.jsx — only routes that
// actually exist are listed. Unknown referenceType falls back to the
// generic href chain in normalizeNotification.
const REFERENCE_TYPE_TO_ROUTE = {
  facility: (uuid) => (uuid ? `/facilities/${uuid}` : ROUTES.FACILITIES),
  device: (uuid) => (uuid ? `/sensors/details/${uuid}` : ROUTES.SENSORS),
  alarm_profile: (uuid) => (uuid ? `/alarms/details/${uuid}` : ROUTES.ALARMS),
}

export function severityForNotification(row) {
  // Type wins over severity: a CALIBRATION_EXPIRY notification is
  // always a calibration regardless of what severity the backend
  // stamps on it. Only fall through to severity / type-lookup-against-
  // SEVERITY_META when no type-specific meta is registered.
  const typeKey = (row?.notificationType || row?.type || '').toUpperCase()
  if (typeKey && NOTIFICATION_TYPE_META[typeKey]) {
    return NOTIFICATION_TYPE_META[typeKey]
  }
  // Backend uses `priority` (LOW/HIGH) rather than `severity`. Map
  // those to the visual scale so HIGH-priority calibration expiries
  // get the eye-catching warning treatment instead of the muted INFO
  // bell.
  const priority = (row?.priority || '').toUpperCase()
  if (
    priority === 'HIGH' ||
    priority === 'CRITICAL' ||
    priority === 'URGENT' ||
    priority === 'MEDIUM' ||
    priority === 'WARNING'
  ) {
    return { ...SEVERITY_META.WARNING, Icon: AlertTriangle }
  }
  const raw = (row?.severity || row?.type || '').toUpperCase()
  if (SEVERITY_META[raw]) {
    // Attach the matching Icon component so consumers can render it
    // without an extra switch.
    const Icon = {
      CRITICAL: ShieldAlert,
      WARNING: AlertTriangle,
      INFO: Bell,
      OFFLINE: Wifi,
      RESOLVED: Check,
      TEMPERATURE: Thermometer,
    }[raw]
    return Icon ? { ...SEVERITY_META[raw], Icon } : SEVERITY_META[raw]
  }
  // Fallback so unrecognised rows still render something readable.
  return { ...SEVERITY_META.INFO, Icon: Bell }
}

export function normalizeNotification(row, idx) {
  // Prefer the explicit link/actionUrl/module chain so any backend
  // row that already provides a destination still wins. Otherwise
  // route via `referenceType` + `referenceUuid` (now populated by the
  // notification-broadcast service for the three new fan-out types).
  const refType = row?.referenceType || null
  const refUuid = row?.referenceUuid || null
  const refRoute = refType ? REFERENCE_TYPE_TO_ROUTE[refType] : null
  const refHref = refRoute ? refRoute(refUuid) : null

  // Backend uses `notificationType` / `priority` rather than `type` /
  // `severity`, so walk both spellings when looking up meta.
  const typeKey = (row?.notificationType || row?.type || '').toUpperCase()
  const priorityKey = (row?.priority || '').toUpperCase()
  const typeMeta = typeKey && NOTIFICATION_TYPE_META[typeKey]

  // The actual notification body. This backend sends the content as
  // `notificationText`; other endpoints might use `description`,
  // `body`, `message`, etc. — walk the chain so the listing always
  // surfaces something readable instead of an empty line.
  const notificationText =
    row?.notificationText ||
    row?.description ||
    row?.body ||
    row?.message ||
    row?.content ||
    row?.text ||
    ''

  // Title / description split. The backend stores the human-readable
  // sentence as `notificationText` (e.g. `Facility "skdnf" was
  // created by PIJUS GHOSH.`) but doesn't ship a separate title,
  // so we promote a short professional label from `NOTIFICATION_TYPE_META`
  // ("New facility", "Calibration", "Alarm profile") to the title
  // and use the full sentence as the description. Falls back to the
  // legacy chain (explicit title/subject → formatted notificationType
  // → "Notification") for any unrecognised type.
  const explicitTitle = row?.title || row?.subject
  const fallbackTitle =
    typeMeta?.label ||
    (row?.module ? String(row.module).replace(/_/g, ' ') : null) ||
    (row?.notificationType
      ? String(row.notificationType)
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : null) ||
    'Notification'

  // Title = short label (explicit → type meta → formatted fallback).
  // Description = full notificationText when we used a label as the
  // title, OR the explicit title when one was supplied. Empty when
  // both are missing so the row shows a single-line title cleanly.
  let title
  let description = ''
  if (explicitTitle) {
    title = explicitTitle
    description = notificationText
  } else if (typeMeta?.label) {
    title = typeMeta.label
    description = notificationText
  } else if (notificationText) {
    // Unrecognised type with no label to lean on — fall back to the
    // notificationText as the title and leave description empty so
    // the listing doesn't render the same sentence twice.
    title = notificationText
    description = ''
  } else {
    title = fallbackTitle
    description = ''
  }

  // Source label — the user who performed the action. The backend
  // resolves `createdBy` (numeric user id) to `createdByName` via the
  // user service at read time. Older payloads may still surface the
  // name through alternate shapes; we walk the chain so callers don't
  // lose information while the rollout completes.
  const source =
    row?.createdByName ||
    row?.source?.name ||
    row?.sourceName ||
    row?.actor?.name ||
    row?.from ||
    row?.branchName ||
    null

  return {
    id: row?.id ?? row?.uuid ?? `ntf-${idx}`,
    title,
    description,
    time: row?.createdAt || row?.updatedAt,
    isRead: Boolean(row?.isRead ?? row?.read ?? row?.readAt),
    severity: severityForNotification(row),
    // Category pill: prefer the type meta label ("New facility", etc.),
    // then the formatted priority ("High priority" / "Low priority"),
    // then the formatted notificationType ("System"), then the raw
    // category/type, then "INFO".
    category:
      typeMeta?.label ||
      (priorityKey
        ? `${priorityKey.charAt(0)}${priorityKey.slice(1).toLowerCase()} priority`
        : null) ||
      (row?.notificationType
        ? String(row.notificationType)
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase())
        : null) ||
      row?.category ||
      row?.type ||
      'INFO',
    priority: priorityKey || '',
    notificationType: typeKey || '',
    source,
    href:
      row?.link ||
      row?.actionUrl ||
      refHref ||
      (row?.module ? `/${String(row.module).toLowerCase()}` : ROUTES.DASHBOARD),
    raw: row,
  }
}

export const PRIORITY_OPTIONS = [
  { id: '', name: 'Any priority' },
  { id: 'HIGH', name: 'High' },
  { id: 'MEDIUM', name: 'Medium' },
  { id: 'LOW', name: 'Low' },
]

export const TYPE_OPTIONS = [
  { id: '', name: 'Any type' },
  { id: 'FACILITY_CREATED', name: 'New facility' },
  { id: 'CALIBRATION_EXPIRY', name: 'Calibration' },
  { id: 'ALARM_PROFILE_STATUS_CHANGED', name: 'Alarm profile' },
]

export const READ_OPTIONS = [
  { id: '', name: 'Any status' },
  { id: 'false', name: 'Unread' },
  { id: 'true', name: 'Read' },
]
